import { daysSince } from '../components/common/relativeAge';
/**
 * How old a keyword's demand numbers are, and how loudly to say so.
 *
 * The Trends table used to present a growth figure pulled four months ago as if
 * it were today's news. The fix is a window plus a visible age — NOT a decayed
 * "hotness" score. A score would need date arithmetic in the SQL sort, and more
 * importantly it would invent a ranking number the reader cannot check against
 * anything: they would see rows reorder and have no way to ask why. A window
 * they chose, and an age they can read, says the same thing and stays honest.
 *
 * Dependency-free on purpose. CRA's jest cannot resolve react-router-dom v7 or
 * react-dnd, so anything worth testing has to live in a module that imports
 * neither — same reason `subjectTree.ts` and `artifactGroups.ts` are separate.
 */

/** Windows offered in the picker. `null` = no window, show everything. */
export const FRESHNESS_WINDOWS: { label: string; days: number | null }[] = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 14 days', days: 14 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'Any age', days: null },
];

/**
 * 30 days, matching DAYS_BETWEEN_RETRY_OF_KEYWORD on the backend.
 *
 * Anything shorter is actively misleading: Google is not re-asked about a
 * keyword within 30 days, so a 7-day default would hide keywords that are
 * simply inside their cache window and tell a busy user they had researched
 * nothing this month.
 */
export const DEFAULT_FRESHNESS_DAYS = 30;

export type AgeBucket = 'fresh' | 'recent' | 'aging' | 'stale';

/** Re-exported, not reimplemented. The SERP timestamps needed the same sum, and
 *  two copies of "how old is this" drift into two vocabularies — which is
 *  exactly what happened before `common/relativeAge` existed. */
export { daysSince as daysOld } from '../components/common/relativeAge';

/**
 * Where this row sits inside the chosen window.
 *
 * Bucketed as a FRACTION of the window, not at fixed day counts, so the fade
 * means the same thing whichever window is selected: "fresh" is the first
 * quarter of what you asked to see, "stale" is the last quarter, and a row
 * about to drop out looks like it. Fixed thresholds would make every row
 * "fresh" on a 90-day window and every row "stale" on a 7-day one.
 */
export function ageBucket(
  collectedAt: string | null | undefined,
  windowDays: number | null,
  now: Date = new Date(),
): AgeBucket {
  const age = daysSince(collectedAt, now);
  // No timestamp at all — a legacy row. Treated as the oldest thing on screen
  // rather than the newest, because "we do not know when this was pulled" is
  // much closer to stale than to fresh.
  if (age === null) return 'stale';

  // No window: bucket against the 30-day re-ask period, so the chips still
  // carry meaning when the user has asked to see everything.
  const span = windowDays ?? DEFAULT_FRESHNESS_DAYS;
  const ratio = span <= 0 ? 1 : age / span;
  if (ratio <= 0.25) return 'fresh';
  if (ratio <= 0.5) return 'recent';
  if (ratio <= 0.75) return 'aging';
  return 'stale';
}

/** Row opacity for a bucket — this IS the fade. */
export function fadeClass(bucket: AgeBucket): string {
  switch (bucket) {
    case 'fresh':
      return '';
    case 'recent':
      return 'opacity-90';
    case 'aging':
      return 'opacity-75';
    case 'stale':
    default:
      return 'opacity-60';
  }
}

/** Chip colour. Deliberately grey-to-amber, never red: an old pull is not an error. */
export function ageChipClass(bucket: AgeBucket): string {
  switch (bucket) {
    case 'fresh':
      return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
    case 'recent':
      return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    case 'aging':
      return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    case 'stale':
    default:
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
  }
}

/** "today" / "3d" / "2mo". Shared with SERP timestamps so one table cannot show
 *  a SERP fetched "2h ago" beside a keyword pulled "today". */
export { relativeAge as ageLabel, exactDate } from '../components/common/relativeAge';

export type EmptyReason = 'window' | 'filters' | 'both' | 'nothing-researched';

/**
 * Why the keyword table is empty, in the order the user can act on.
 *
 * "Try adjusting your filters" was wrong in the most common new case. The
 * freshness window is NOT one of the filters — it sits outside the disclosure,
 * beside the section heading — so someone who narrowed to 7 days and got
 * nothing was pointed at controls they never touched, while the real cause sat
 * elsewhere on screen and went unnamed.
 *
 * Same failure as the brainstorm list's empty state: a filtered-to-nothing
 * branch that describes the wrong filter is worse than no message, because it
 * sends the reader to go and check something that was never the problem.
 */
export function emptyReason(opts: {
  windowDays: number | null;
  hasFilters: boolean;
}): EmptyReason {
  const windowed = opts.windowDays !== null;
  if (windowed && opts.hasFilters) return 'both';
  if (windowed) return 'window';
  if (opts.hasFilters) return 'filters';
  // No window and no filters: there is genuinely nothing to show, which for
  // this table means no keyword has ever been pulled from Google Ads.
  return 'nothing-researched';
}

export function emptyMessage(reason: EmptyReason, windowDays: number | null): string {
  switch (reason) {
    case 'window':
      return `No keywords pulled from Google Ads in the last ${windowDays} days.`;
    case 'both':
      return `No keywords match those filters in the last ${windowDays} days.`;
    case 'filters':
      return 'No keywords match those filters.';
    case 'nothing-researched':
    default:
      return 'No keyword research yet - research a trending story above, or discover keywords inside a subject.';
  }
}

/**
 * Whether the URL arrived carrying a filter, so the Filters panel must open.
 *
 * The panel is collapsed by default now that the cards above it are the point.
 * But four inbound deep-links apply a filter through the URL — the brainstorm
 * room's `?scope=explored&seed=…`, the Dashboard's `?recommendations_only=true`
 * and `?keyword_search=…`, and the `/recommendations` redirect — and a collapsed
 * panel would hand you a filtered table with no visible reason for what is
 * missing. You would be looking at a subset and have no way to know it.
 *
 * Separate from `hasActiveFilters`, which only tracks the text and semantic
 * boxes: this is about ARRIVING filtered, and covers the params those two do
 * not.
 */
export function arrivedFiltered(params: {
  get(key: string): string | null;
}): boolean {
  return Boolean(
    params.get('scope') ||
      params.get('seed') ||
      params.get('keyword_search') ||
      params.get('recommendations_only') === 'true',
  );
}

export type TrendsTab = 'stories' | 'keywords';

/**
 * The two datasets — and they are NAV ENTRIES, not tabs on a page.
 *
 * They used to be a third layer: Research › Trends › (Trending now | Your
 * research). Three layers to reach a list is a layer too many, and the middle
 * one — "Trends" — named nothing you could look at; it was a lobby. So each is
 * a sibling of Subjects now, and each has its own PATH rather than a `?tab=`,
 * because a nav entry that cannot be told apart by pathname cannot be
 * highlighted.
 *
 * `?tab=` still resolves: four deep links in the app carry keyword filters and
 * every one of them lands on `/trending`. `canonicalTrendsPath` is how those
 * arrive at the right entry rather than at the right table under the wrong
 * heading.
 */
export const TRENDS_TABS: {
  key: TrendsTab;
  path: string;
  label: string;
  hint: string;
}[] = [
  {
    key: 'stories',
    path: '/trending',
    label: 'Trending now',
    hint: "What the country is searching, researched into pages you could build",
  },
  {
    key: 'keywords',
    path: '/trending/keywords',
    // NOT "Your research", and the hint said the same thing: "keywords you have
    // pulled". Both claim an authorship the table cannot vouch for — a trending
    // run pulls keywords from Google Ads on your behalf, so rows appear here
    // that nobody remembers asking for. Told they are "yours", the honest
    // reaction is to distrust the screen rather than to learn what filled it.
    //
    // "Recent" claims only what is true: these are the newest, and the hint now
    // names the other way they arrive.
    label: 'Recent',
    hint: 'Keywords in your account, freshest first - including ones a trending run pulled for you',
  },
];

/** Where a tab lives, for a link or a redirect. */
export function trendsTabPath(tab: TrendsTab): string {
  return TRENDS_TABS.find((t) => t.key === tab)?.path || '/trending';
}

/**
 * Which tab the URL means.
 *
 * Validated against the list rather than cast, for the reason the subject
 * workspace already learned the hard way: an unchecked cast renders NOTHING for
 * an unknown value — no tab highlighted, no panel, no error.
 *
 * A DEEP-LINK THAT FILTERS THE TABLE SELECTS THE TABLE. Four links in the app
 * carry keyword filters — the brainstorm room's `?scope=explored&seed=…`, the
 * Dashboard's `?recommendations_only=true` and `?keyword_search=…`, and the
 * `/recommendations` redirect. Landing them on the stories tab would apply a
 * filter to a table the reader cannot see, which is the same class of mistake
 * as filtering into a collapsed panel.
 */
export function resolveTrendsTab(
  params: { get(key: string): string | null },
  pathname: string = '',
): TrendsTab {
  // THE PATH WINS. It is what the nav highlights and what a reload restores;
  // a stale `?tab=stories` riding along on a link into the keyword list would
  // otherwise send you to the other one.
  const byPath = TRENDS_TABS.find((t) => t.path !== '/trending' && pathname === t.path);
  if (byPath) return byPath.key;
  const raw = params.get('tab');
  const known = TRENDS_TABS.find((t) => t.key === raw);
  if (known) return known.key;
  return arrivedFiltered(params) ? 'keywords' : 'stories';
}
